import { useState } from "react";

import { cn } from "@/lib/utils/cn";

interface AuroraBackgroundProps {
  className?: string;
}

type ParticleTone = "primary" | "secondary";

type Particle = {
  id: number;
  left: number;
  top: number;
  size: number;
  duration: number;
  delay: number;
  rise: number;
  drift: number;
  peakOpacity: number;
  tone: ParticleTone;
};

const PARTICLE_COUNT = 18;

function createParticles(count: number): Particle[] {
  return Array.from({ length: count }, (_, id) => {
    const riseMagnitude = 120 + Math.random() * 160;
    return {
      id,
      left: Math.random() * 100,
      top: Math.random() * 100,
      size: 2 + Math.random() * 3,
      duration: 16 + Math.random() * 14,
      delay: -Math.random() * 30,
      rise: Math.random() < 0.5 ? riseMagnitude : -riseMagnitude,
      drift: (Math.random() - 0.5) * 160,
      peakOpacity: 0.25 + Math.random() * 0.3,
      tone: id % 2 === 0 ? "primary" : "secondary",
    };
  });
}

export const AuroraBackground: React.FC<AuroraBackgroundProps> = ({
  className,
}) => {
  const [particles] = useState(() => createParticles(PARTICLE_COUNT));

  return (
    <div
      className={cn("absolute inset-0 overflow-hidden", className)}
      aria-hidden="true"
    >
      <div
        className="absolute -top-[15%] left-[8%] h-[50vw] w-[50vw] max-h-130 max-w-130 rounded-full bg-primary-300/16 blur-[90px] will-change-transform animate-[aurora-drift-a_30s_ease-in-out_infinite] motion-reduce:animate-none"
      />
      <div
        className="absolute top-[5%] right-[5%] h-[46vw] w-[46vw] max-h-115 max-w-115 rounded-full bg-archive-400/12 blur-[100px] will-change-transform animate-[aurora-drift-b_34s_ease-in-out_infinite] motion-reduce:animate-none"
      />
      <div className="absolute left-1/2 top-1/2 h-[56vw] w-[56vw] max-h-155 max-w-155 -translate-x-1/2 -translate-y-1/2">
        <div
          className="h-full w-full rounded-full bg-primary-600/10 blur-[110px] will-change-transform animate-[aurora-drift-c_26s_ease-in-out_infinite] motion-reduce:animate-none"
        />
      </div>
      <div
        className="absolute bottom-[-12%] left-[15%] h-[40vw] w-[40vw] max-h-100 max-w-100 rounded-full bg-archive-300/14 blur-[95px] will-change-transform animate-[aurora-drift-d_32s_ease-in-out_infinite] motion-reduce:animate-none"
      />

      {particles.map((particle) => (
        <span
          key={particle.id}
          className={cn(
            "absolute rounded-full motion-reduce:hidden",
            particle.tone === "primary" ? "bg-primary-500" : "bg-archive-400",
          )}
          style={
            {
              left: `${particle.left}%`,
              top: `${particle.top}%`,
              width: particle.size,
              height: particle.size,
              opacity: 0,
              animationName: "aurora-particle-float",
              animationDuration: `${particle.duration}s`,
              animationDelay: `${particle.delay}s`,
              animationTimingFunction: "ease-in-out",
              animationIterationCount: "infinite",
              "--particle-rise": `${particle.rise}px`,
              "--particle-drift": `${particle.drift}px`,
              "--particle-peak-opacity": particle.peakOpacity,
            } as React.CSSProperties
          }
        />
      ))}
    </div>
  );
};
